<template>
  <div class="step-one-batch">
    <!-- 配置默认车型信息 -->
    <GlobalCarInfo ref="globalCarInfoRef" @carAgeChange="handleCarAgeChange" />

    <!-- 选择租赁方案（车龄选择后显示） -->
    <CarModelTable
      v-if="hasCarAge"
      ref="carModelTableRef"
      @openPlanDialog="handleOpenPlanDialog"
      @openCarInfoDialog="handleOpenCarInfoDialog"
    />

    <!-- 未选择车龄时的占位 -->
    <div class="car-model-placeholder section-card" v-else>
      <div class="section-header">
        <span class="section-title">选择租赁方案</span>
      </div>
      <el-empty description="暂无数据"></el-empty>
    </div>

    <!-- 租赁方案选择弹窗 -->
    <PlanSelectDialog ref="planSelectDialogRef" @confirm="handlePlanConfirm" />

    <!-- 车型信息设置弹窗 -->
    <CarInfoSettingDialog
      ref="carInfoSettingDialogRef"
      @confirm="handleCarInfoConfirm"
    />
  </div>
</template>

<script setup>
import { setBatchCarConfig } from '@/api/goods'
import { Message, MessageBox } from '@qs/ui'
import { computed, ref } from 'vue'
import CarInfoSettingDialog from './components/CarInfoSettingDialog.vue'
import CarModelTable from './components/CarModelTable.vue'
import GlobalCarInfo from './components/GlobalCarInfo.vue'
import PlanSelectDialog from './components/PlanSelectDialog.vue'
import { useBatchSession } from './hooks/useBatchSession'

const { sessionId, globalCarInfo, setCarAge, setGlobalCarInfo } =
  useBatchSession()

// 是否已选择车龄（支持 0 作为有效值）
const hasCarAge = computed(() => {
  const carAge = globalCarInfo.value?.carAge
  return carAge !== null && carAge !== undefined && carAge !== ''
})

// 组件 refs
const globalCarInfoRef = ref(null)
const carModelTableRef = ref(null)
const planSelectDialogRef = ref(null)
const carInfoSettingDialogRef = ref(null)

// 判断是否有有效的车龄值
const isValidCarAge = (val) => val !== null && val !== undefined && val !== ''

// 车龄变更处理
const handleCarAgeChange = async (newAge, oldAge) => {
  // 如果之前有有效的车龄值，需要确认（oldAge 支持 0）
  if (isValidCarAge(oldAge)) {
    // 判断是删除还是切换
    const isDelete = !isValidCarAge(newAge)
    const action = isDelete ? '删除' : '切换'
    const message = isDelete
      ? '删除车龄后，已编辑内容将丢失，确认继续？'
      : '切换车龄后，租赁方案列表数据会重新刷新，已编辑内容将丢失，确认继续？'

    try {
      await MessageBox.confirm(message, `确认${action}车龄吗？`, {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning',
      })
    } catch {
      // 用户取消，恢复原车龄
      globalCarInfoRef.value?.setCarAge(oldAge)
      return
    }
  }

  // 更新 Vuex 中的车龄
  setCarAge(newAge)

  // 刷新车型列表（仅当有有效车龄时）
  if (isValidCarAge(newAge)) {
    carModelTableRef.value?.refresh()
  }
}

// 打开租赁方案选择弹窗
const handleOpenPlanDialog = (row) => {
  planSelectDialogRef.value?.open(row)
}

// 租赁方案选择确认
const handlePlanConfirm = () => {
  carModelTableRef.value?.refresh()
}

// 打开车型信息设置弹窗
const handleOpenCarInfoDialog = (row) => {
  carInfoSettingDialogRef.value?.open(row)
}

// 车型信息设置确认
const handleCarInfoConfirm = () => {
  carModelTableRef.value?.refresh()
}

// 校验
const validate = async () => {
  // 校验全局车型信息（车龄、表显里程、车证）
  await globalCarInfoRef.value?.validate()
}

// 下一步钩子
const onNext = async () => {
  // 获取全局车型配置
  const carInfo = globalCarInfoRef.value?.getFormData()

  if (!carInfo) {
    Message.error('请完善车型信息')
    return Promise.reject(new Error('请完善车型信息'))
  }

  // 保存全局配置到服务端
  const res = await setBatchCarConfig({
    sessionId: sessionId.value,
    mileage: carInfo.mileage,
    carCert: carInfo.hasLicense ? 1 : 0,
  })

  if (res?.code !== 1) {
    throw new Error('保存配置失败')
  }

  // 同步更新 Vuex（确保状态一致）
  setGlobalCarInfo({
    carAge: carInfo.carAge,
    mileage: carInfo.mileage,
    hasLicense: carInfo.hasLicense,
  })
}

// 刷新车型列表
const refresh = () => {
  if (isValidCarAge(globalCarInfo.value?.carAge)) {
    carModelTableRef.value?.refresh()
  }
}

// 步骤被激活时的钩子
const onActivated = () => {
  refresh()
  return Promise.resolve()
}

defineExpose({
  validate,
  onNext,
  onActivated,
  refresh, // 保留向后兼容
})
</script>

<style lang="scss" scoped>
.step-one-batch {
  .car-model-placeholder {
    .section-header {
      margin-bottom: 16px;

      .section-title {
        font-size: 16px;
        font-weight: 600;
        line-height: 24px;
        color: #1d2129;
      }
    }

    ::v-deep .el-empty {
      padding: 40px 0;
    }
  }
}
</style>
